import React, { useState, useRef, useEffect } from 'react';

const GooeySkillItem = ({ children }) => {
  const [isHovered, setIsHovered] = useState(false);
  const itemRef = useRef(null);
  const textRef = useRef(null);
  const canvasRef = useRef(null);
  const backgroundRef = useRef(null);
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
  const hoverTimeoutRef = useRef(null);
  
  // Orange color for the hover effect and painting
  const hoverColor = '#FF8A00'; // Bright orange

  // Handle mouse movement to both update deformation and paint on canvas
  const handleMouseMove = (e) => {
    if (!itemRef.current || !canvasRef.current) return;

    const rect = itemRef.current.getBoundingClientRect();
    // Compute relative percentage values for deformation effects
    const percentX = ((e.clientX - rect.left) / rect.width) * 100;
    const percentY = ((e.clientY - rect.top) / rect.height) * 100;
    setMousePosition({ x: percentX, y: percentY });
    
    // Also paint on the canvas
    const ctx = canvasRef.current.getContext('2d');
    // Compute the pixel coordinates relative to the container
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    // Draw a small filled circle at the current cursor position
    ctx.fillStyle = hoverColor;
    ctx.beginPath();
    ctx.arc(x, y, 5, 0, Math.PI * 2);
    ctx.fill();
  };

  const handleMouseEnter = (e) => {
    // Clear any existing timeout to prevent state conflicts
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setIsHovered(true);
    
    // Prepare the canvas dimensions to match the container
    if (canvasRef.current && itemRef.current) {
      const { clientWidth, clientHeight } = itemRef.current;
      canvasRef.current.width = clientWidth;
      canvasRef.current.height = clientHeight;
      // Clear any previous drawings
      const ctx = canvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
  };

  const handleMouseLeave = (e) => {
    // Check if the mouse is still over any part of the component
    // including the background
    if (backgroundRef.current) {
      const bgRect = backgroundRef.current.getBoundingClientRect();
      const isOverBackground = 
        e.clientX >= bgRect.left &&
        e.clientX <= bgRect.right &&
        e.clientY >= bgRect.top &&
        e.clientY <= bgRect.bottom;
      
      // If still over the background, don't turn off hover state yet
      if (isOverBackground) return;
    }
    
    // Use a longer timeout for a smoother fade-out
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    
    hoverTimeoutRef.current = setTimeout(() => {
      if (textRef.current && itemRef.current && backgroundRef.current) {
        const textRect = textRef.current.getBoundingClientRect();
        const itemRect = itemRef.current.getBoundingClientRect();
        const bgRect = backgroundRef.current.getBoundingClientRect();
        
        const mouseX = window.event?.clientX || 0;
        const mouseY = window.event?.clientY || 0;
        
        // Add a buffer zone (20px)
        const buffer = 20;
        
        const isOverText = (
          mouseX >= textRect.left - buffer &&
          mouseX <= textRect.right + buffer &&
          mouseY >= textRect.top - buffer &&
          mouseY <= textRect.bottom + buffer
        );
        const isOverItem = (
          mouseX >= itemRect.left - buffer &&
          mouseX <= itemRect.right + buffer &&
          mouseY >= itemRect.top - buffer &&
          mouseY <= itemRect.bottom + buffer
        );
        const isOverBackground = (
          mouseX >= bgRect.left - buffer &&
          mouseX <= bgRect.right + buffer &&
          mouseY >= bgRect.top - buffer &&
          mouseY <= bgRect.bottom + buffer
        );

        if (!isOverText && !isOverItem && !isOverBackground) {
          setIsHovered(false);
          // Clear the canvas when no longer hovered
          if (canvasRef.current) {
            const ctx = canvasRef.current.getContext('2d');
            ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
          }
        }
      } else {
        setIsHovered(false);
        // Clear the canvas when no longer hovered
        if (canvasRef.current) {
          const ctx = canvasRef.current.getContext('2d');
          ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        }
      }
    }, 150); // Longer timeout for smoother transition
  };

  // Attach or remove the mousemove event listener as needed
  useEffect(() => {
    if (!isHovered) return;
    const item = itemRef.current;
    if (!item) return;
    
    item.addEventListener('mousemove', handleMouseMove);
    return () => {
      item.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isHovered]);

  // Add a document-level mouse move handler to detect when mouse is far from the element
  useEffect(() => {
    if (!isHovered) return;
    
    const handleDocumentMouseMove = (e) => {
      if (!itemRef.current || !textRef.current || !backgroundRef.current) return;
      
      const itemRect = itemRef.current.getBoundingClientRect();
      const textRect = textRef.current.getBoundingClientRect();
      const bgRect = backgroundRef.current.getBoundingClientRect();
      
      // Add a larger buffer zone (20px)
      const buffer = 20;
      
      // Check if mouse is far from all elements
      const isFarFromItem = 
        e.clientX < itemRect.left - buffer || 
        e.clientX > itemRect.right + buffer || 
        e.clientY < itemRect.top - buffer || 
        e.clientY > itemRect.bottom + buffer;
        
      const isFarFromText = 
        e.clientX < textRect.left - buffer || 
        e.clientX > textRect.right + buffer || 
        e.clientY < textRect.top - buffer || 
        e.clientY > textRect.bottom + buffer;
        
      const isFarFromBackground = 
        e.clientX < bgRect.left - buffer || 
        e.clientX > bgRect.right + buffer || 
        e.clientY < bgRect.top - buffer || 
        e.clientY > bgRect.bottom + buffer;
      
      // If mouse is far from all elements, turn off hover state
      if (isFarFromItem && isFarFromText && isFarFromBackground) {
        // Use a timeout for smoother transition
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
        }
        
        hoverTimeoutRef.current = setTimeout(() => {
          setIsHovered(false);
          // Clear the canvas when no longer hovered
          if (canvasRef.current) {
            const ctx = canvasRef.current.getContext('2d');
            ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
          }
          hoverTimeoutRef.current = null;
        }, 150); // Longer timeout for smoother transition
      }
    };
    
    document.addEventListener('mousemove', handleDocumentMouseMove);
    
    return () => {
      document.removeEventListener('mousemove', handleDocumentMouseMove);
    };
  }, [isHovered]);

  // Clean up any lingering timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // Calculate deformation for the gooey effect based on mouse position
  const calculateDeformation = () => {
    if (!isHovered) return {};
    
    // Calculate distance from each corner to create a bulging effect
    const topLeft = Math.sqrt(mousePosition.x ** 2 + mousePosition.y ** 2);
    const topRight = Math.sqrt((100 - mousePosition.x) ** 2 + mousePosition.y ** 2);
    const bottomLeft = Math.sqrt(mousePosition.x ** 2 + (100 - mousePosition.y) ** 2);
    const bottomRight = Math.sqrt((100 - mousePosition.x) ** 2 + (100 - mousePosition.y) ** 2);
    
    // Normalize distances (closer = more deformation)
    const maxDistance = Math.sqrt(2) * 100;
    const tl = 20 + (1 - topLeft / maxDistance) * 30;
    const tr = 20 + (1 - topRight / maxDistance) * 30;
    const bl = 20 + (1 - bottomLeft / maxDistance) * 30;
    const br = 20 + (1 - bottomRight / maxDistance) * 30;
    
    return {
      borderRadius: `${tl}px ${tr}px ${br}px ${bl}px`
    };
  };

  return (
    <div
      ref={itemRef}
      className={`skill-item ${isHovered ? 'hovered' : ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        position: 'relative', // Ensure positioning context for canvas
        '--mouse-x': `${mousePosition.x}%`,
        '--mouse-y': `${mousePosition.y}%`,
        '--hover-color': hoverColor, // Pass the color as a CSS variable
        transition: 'all 0.5s cubic-bezier(0.34, 0.56, 0.64, 1)', // Add transition for smoother changes
        ...calculateDeformation()
      }}
    >
      {/* Background element with ref for hover detection */}
      <div 
        ref={backgroundRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: isHovered ? hoverColor : 'transparent',
          opacity: isHovered ? 1 : 0,
          transition: 'opacity 0.5s ease, background-color 0.5s ease', // Smooth transition
          borderRadius: 'inherit',
          zIndex: 1
        }}
      />
      
      {/* Canvas overlay for painting (ensures pointer events pass through) */}
      {isHovered && (
        <canvas
          ref={canvasRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            zIndex: 2,
            opacity: isHovered ? 1 : 0,
            transition: 'opacity 0.5s ease' // Smooth fade out
          }}
        />
      )}
      
      <span 
        ref={textRef}
        className="skill-text"
        style={{ 
          position: 'relative',
          zIndex: 5,
          color: isHovered ? '#000000' : 'inherit',
          transition: 'color 0.5s ease' // Smooth color transition
        }}
      >
        {children}
      </span>
      
      {/* SVG filter for gooey effect */}
      {isHovered && (
        <svg width="0" height="0" style={{ position: 'absolute', top: 0, left: 0 }}>
          <defs>
            <filter id="gooey">
              <feGaussianBlur in="SourceGraphic" stdDeviation="5" result="blur" />
              <feColorMatrix in="blur" mode="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 18 -7" result="gooey" />
            </filter>
          </defs>
        </svg>
      )}
    </div>
  );
};

export default GooeySkillItem;